import React from "react";
import { useNavigate } from "react-router-dom";

// Minimal, safe markdown: paragraphs, **bold**, bullet and numbered lists,
// [text](href) links and bare URLs. In-app routes navigate without a reload.

const INLINE_RE =
  /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)\s]+)\))|(https?:\/\/[^\s)]+)|(`([^`]+)`)/g;

function isSafeHref(href: string) {
  return /^(https?:|mailto:|tel:)/i.test(href) || href.startsWith("/");
}

function InlineLink({ href, children }: { href: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  if (!isSafeHref(href)) return <>{children}</>;
  if (href.startsWith("/")) {
    return (
      <a
        href={href}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          navigate(href);
        }}
        className="font-medium text-[#0F5132] underline decoration-emerald-300 underline-offset-2 hover:decoration-[#0F5132]"
      >
        {children}
      </a>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-[#0F5132] underline decoration-emerald-300 underline-offset-2 hover:decoration-[#0F5132] break-all"
    >
      {children}
    </a>
  );
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  INLINE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = INLINE_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyPrefix}-${i++}`;
    if (m[1]) {
      out.push(
        <strong key={k} className="font-semibold text-slate-900">
          {m[2]}
        </strong>
      );
    } else if (m[3]) {
      out.push(
        <InlineLink key={k} href={m[5]}>
          {m[4]}
        </InlineLink>
      );
    } else if (m[6]) {
      out.push(
        <InlineLink key={k} href={m[6]}>
          {m[6]}
        </InlineLink>
      );
    } else if (m[7]) {
      out.push(
        <code key={k} className="rounded bg-slate-100 px-1 py-0.5 text-[12px] text-slate-700">
          {m[8]}
        </code>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { type: "p"; lines: string[] }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[]; start: number };

function parseBlocks(src: string): Block[] {
  const blocks: Block[] = [];
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    const last = blocks[blocks.length - 1];
    if (!line.trim()) {
      blocks.push({ type: "p", lines: [] });
      continue;
    }
    if (bullet) {
      if (last?.type === "ul") last.items.push(bullet[1]);
      else blocks.push({ type: "ul", items: [bullet[1]] });
    } else if (numbered) {
      if (last?.type === "ol") last.items.push(numbered[2]);
      else blocks.push({ type: "ol", items: [numbered[2]], start: Number(numbered[1]) || 1 });
    } else {
      const heading = line.replace(/^#{1,6}\s+/, "");
      const text = heading !== line ? `**${heading}**` : line;
      if (last?.type === "p") last.lines.push(text);
      else blocks.push({ type: "p", lines: [text] });
    }
  }
  return blocks.filter((b) => (b.type === "p" ? b.lines.length > 0 : true));
}

const LightMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const blocks = parseBlocks(text || "");
  return (
    <div className="space-y-2 text-sm leading-relaxed text-slate-700 break-words">
      {blocks.map((b, bi) => {
        if (b.type === "p") {
          return (
            <p key={bi}>
              {b.lines.map((l, li) => (
                <React.Fragment key={li}>
                  {li > 0 && <br />}
                  {renderInline(l, `${bi}-${li}`)}
                </React.Fragment>
              ))}
            </p>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={bi} className="list-disc space-y-1 pl-5 marker:text-slate-400">
              {b.items.map((it, ii) => (
                <li key={ii}>{renderInline(it, `${bi}-${ii}`)}</li>
              ))}
            </ul>
          );
        }
        return (
          <ol key={bi} start={b.start} className="list-decimal space-y-1 pl-5 marker:text-slate-400">
            {b.items.map((it, ii) => (
              <li key={ii}>{renderInline(it, `${bi}-${ii}`)}</li>
            ))}
          </ol>
        );
      })}
    </div>
  );
};

export default LightMarkdown;
